import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { set } from 'react-hook-form';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('connected');
  const [currentBalance, setCurrentBalance] = useState(125847.32);

  const [metamaskAddress, setMetamaskAddress] = useState(null);

  const dropdownRef = useRef(null);

  const menuItems = [
    { id: 'home', label: 'Home', path: '/', icon: 'Home' },
    { id: 'markets', label: 'Markets', path: '/markets', icon: 'TrendingUp' },
    { id: 'dashboard', label: 'Dashboard', path: '/trading-dashboard', icon: 'BarChart3' },
    { id: 'portfolio', label: 'Portfolio', path: '/portfolio-management', icon: 'PieChart' },
    { id: 'orders', label: 'Orders', path: '/order-management', icon: 'List' },
    { id: 'wallet', label: 'Wallet', path: '/wallet-transactions', icon: 'Wallet' },
    { id: 'account', label: 'Account', path: '/account-settings', icon: 'Settings' },
  ];

  const userProfile = {
    name: 'John Trader',
    email: 'john@example.com',
    avatar: '/assets/images/avatar.png',
    verificationLevel: 'verified',
    twoFactorEnabled: true
  };

  useEffect(() => {

    if (localStorage.getItem('metamaskAddress')) {
      setMetamaskAddress(localStorage.getItem('metamaskAddress'));
    } else {
      setMetamaskAddress(null);
    }
    
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBalance(prev => prev + (Math.random() - 0.5) * 100);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleLogout = () => {
    navigate('/login-register');
    setIsProfileDropdownOpen(false);
  };

  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'text-success';
      case 'connecting': return 'text-warning';
      case 'disconnected': return 'text-error';
      default: return 'text-secondary';
    }
  };

  const getConnectionStatusIcon = () => {
    switch (connectionStatus) {
      case 'connected': return 'Wifi';
      case 'connecting': return 'WifiOff';
      case 'disconnected': return 'WifiOff';
      default: return 'Wifi';
    }
  };

  const isActive = (path) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname?.startsWith(path)) return true;
    return false;
  };

  if (location.pathname === '/login-register') {
    return null;
  }

  const metamaskLogout = () => {
    localStorage.removeItem('metamaskAddress');
    setMetamaskAddress(null);
    navigate(``);
  }

  const isLandingPage = location.pathname === '/';

  return (
    <header className="fixed top-0 left-0 right-0 bg-surface border-b border-border z-header backdrop-blur-sm">
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center space-x-8">
            <button 
              onClick={() => navigate('/')}
              className="flex items-center space-x-3 hover:opacity-80 transition-opacity duration-trading-fast"
            >
              <div className="w-8 h-8 bg-gradient-primary rounded-lg flex items-center justify-center glow-primary">
                <Icon name="TrendingUp" size={20} color="white" />
              </div>
              <span className="text-xl font-heading-semibold text-text-primary">
                PredictPro
              </span>
            </button>

            {/* Desktop Navigation - Only show on non-landing pages */}
            {!isLandingPage && (
              <nav className="hidden md:flex items-center space-x-1">
                {menuItems?.slice(1)?.map((item) => (
                  <button
                    key={item?.id}
                    onClick={() => handleNavigation(item?.path)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-trading font-body-medium text-sm transition-all duration-trading-fast ${
                      isActive(item?.path)
                        ? 'bg-primary-900 text-primary-300 border border-primary-800 glow-primary' :'text-text-secondary hover:text-text-primary hover:bg-surface-hover hover:border-border-hover'
                    }`}
                  >
                    <Icon name={item?.icon} size={16} />
                    <span>{item?.label}</span>
                  </button>
                ))}
              </nav>
            )}

            {/* Landing page navigation */}
            {isLandingPage && metamaskAddress && (
              <nav className="hidden md:flex items-center space-x-1">
                <button
                  onClick={() => handleNavigation('/markets')}
                  className="flex items-center space-x-2 px-4 py-2 rounded-trading font-body-medium text-sm text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-all duration-trading-fast"
                >
                  <Icon name="TrendingUp" size={16} />
                  <span>Markets</span>
                </button>
                <button
                  onClick={() => handleNavigation('/trading-dashboard')}
                  className="flex items-center space-x-2 px-4 py-2 rounded-trading font-body-medium text-sm text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-all duration-trading-fast"
                >
                  <Icon name="BarChart3" size={16} />
                  <span>Dashboard</span>
                </button>
              </nav>
            )}
          </div>

          {/* Right Side Controls */}
          <div className="flex items-center space-x-4">
            {!isLandingPage && metamaskAddress && (
              <div className="flex items-center space-x-2">
                <button onClick={() => metamaskLogout()} className="flex items-center space-x-2">
                  <Icon name="LogOut" size={16} className="text-error" />
                </button>
                <span className="inline-block">{metamaskAddress}</span>
              </div>
            )}
            {/* Quick Balance Display - Only show on non-landing pages */}
            {!isLandingPage && (
              <div className="hidden sm:flex items-center space-x-2 px-4 py-2 bg-surface-hover rounded-trading border border-border">
                <Icon name="DollarSign" size={16} className="text-primary" />
                <span className="font-data text-sm text-text-primary">
                  ${currentBalance?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            )}

            {/* Connection Status - Only show on non-landing pages */}
            {!isLandingPage && (
              <div className="flex items-center space-x-2 px-3 py-2 bg-surface-hover rounded-trading border border-border">
                <Icon 
                  name={getConnectionStatusIcon()} 
                  size={16} 
                  className={`${getConnectionStatusColor()} transition-colors duration-trading-fast`}
                />
                <span className={`hidden sm:inline text-xs font-caption-normal ${getConnectionStatusColor()}`}>
                  {connectionStatus?.charAt(0)?.toUpperCase() + connectionStatus?.slice(1)}
                </span>
              </div>
            )}

            {/* Security Status - Only show on non-landing pages */}
            {!isLandingPage && (
              <div className="hidden sm:flex items-center space-x-2 px-3 py-2 bg-surface-hover rounded-trading border border-border">
                <Icon 
                  name={userProfile?.twoFactorEnabled ? 'Shield' : 'ShieldAlert'} 
                  size={16} 
                  className={userProfile?.twoFactorEnabled ? 'text-success' : 'text-warning'}
                />
                <Icon 
                  name={userProfile?.verificationLevel === 'verified' ? 'CheckCircle' : 'AlertCircle'} 
                  size={16} 
                  className={userProfile?.verificationLevel === 'verified' ? 'text-success' : 'text-warning'}
                />
              </div>
            )}

            {/* CTA Buttons for Landing Page */}
            {isLandingPage && metamaskAddress && (
              <div className="hidden sm:flex items-center space-x-3">
                <button
                  onClick={() => navigate('/markets')}
                  className="bg-surface-hover text-text-primary px-4 py-2 rounded-trading font-body-medium text-sm hover:bg-border-hover border border-border transition-colors duration-trading-fast"
                >
                  Explore Markets
                </button>
                <button
                  onClick={() => navigate('/login-register')}
                  className="bg-gradient-primary text-white px-4 py-2 rounded-trading font-body-medium text-sm hover:opacity-90 transition-opacity duration-trading-fast glow-primary"
                >
                  Get Started
                </button>
              </div>
            )}

            {/* User Profile Dropdown - Only show on non-landing pages */}
            {!isLandingPage && (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                  className="flex items-center space-x-2 p-2 rounded-trading hover:bg-surface-hover hover:border-border-hover border border-transparent transition-colors duration-trading-fast"
                >
                  <div className="w-8 h-8 bg-gradient-primary rounded-full flex items-center justify-center glow-primary">
                    <Icon name="User" size={16} color="white" />
                  </div>
                  <span className="hidden sm:inline font-body-medium text-sm text-text-primary">
                    {userProfile?.name}
                  </span>
                  <Icon 
                    name="ChevronDown" 
                    size={16} 
                    className={`text-text-secondary transition-transform duration-trading-fast ${
                      isProfileDropdownOpen ? 'rotate-180' : ''
                    }`}
                  />
                </button>

                {/* Profile Dropdown */}
                {isProfileDropdownOpen && (
                  <div className="absolute right-0 top-full mt-2 w-64 bg-surface border border-border rounded-trading shadow-trading-modal z-dropdown backdrop-blur-sm">
                    <div className="p-4 border-b border-border">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center glow-primary">
                          <Icon name="User" size={20} color="white" />
                        </div>
                        <div>
                          <p className="font-body-medium text-sm text-text-primary">{userProfile?.name}</p>
                          <p className="font-caption-normal text-xs text-text-secondary">{userProfile?.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4 mt-3">
                        <div className="flex items-center space-x-1">
                          <Icon 
                            name={userProfile?.twoFactorEnabled ? 'Shield' : 'ShieldAlert'} 
                            size={14} 
                            className={userProfile?.twoFactorEnabled ? 'text-success' : 'text-warning'}
                          />
                          <span className="text-xs text-text-secondary">2FA</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Icon 
                            name={userProfile?.verificationLevel === 'verified' ? 'CheckCircle' : 'AlertCircle'} 
                            size={14} 
                            className={userProfile?.verificationLevel === 'verified' ? 'text-success' : 'text-warning'}
                          />
                          <span className="text-xs text-text-secondary">Verified</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-2">
                      <button
                        onClick={() => {
                          handleNavigation('/account-settings');
                          setIsProfileDropdownOpen(false);
                        }}
                        className="w-full flex items-center space-x-2 px-3 py-2 text-left rounded-trading hover:bg-surface-hover transition-colors duration-trading-fast"
                      >
                        <Icon name="Settings" size={16} className="text-text-secondary" />
                        <span className="font-body-normal text-sm text-text-primary">Account Settings</span>
                      </button>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center space-x-2 px-3 py-2 text-left rounded-trading hover:bg-error-900 hover:bg-opacity-20 transition-colors duration-trading-fast"
                      >
                        <Icon name="LogOut" size={16} className="text-error" />
                        <span className="font-body-normal text-sm text-error">Sign Out</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-trading hover:bg-surface-hover border border-transparent hover:border-border-hover transition-colors duration-trading-fast"
            >
              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={20} className="text-text-primary" />
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pt-4 border-t border-border">
            <nav className="space-y-2">
              {menuItems?.map((item) => (
                <button
                  key={item?.id}
                  onClick={() => handleNavigation(item?.path)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-trading font-body-medium text-sm transition-all duration-trading-fast ${
                    isActive(item?.path)
                      ? 'bg-primary-900 text-primary-300 border border-primary-800 glow-primary' :'text-text-secondary hover:text-text-primary hover:bg-surface-hover hover:border-border-hover'
                  }`}
                >
                  <Icon name={item?.icon} size={18} />
                  <span>{item?.label}</span>
                </button>
              ))}
            </nav>
            
            {/* Mobile Balance Display - Only show on non-landing pages */}
            {!isLandingPage && (
              <div className="mt-4 p-4 bg-surface-hover rounded-trading border border-border">
                <div className="flex items-center justify-between">
                  <span className="font-body-medium text-sm text-text-secondary">Account Balance</span>
                  <span className="font-data text-lg text-text-primary">
                    ${currentBalance?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            )}

            {/* Mobile CTA for Landing Page */}
            {isLandingPage && (
              <div className="mt-4 space-y-2">
                <button
                  onClick={() => navigate('/markets')}
                  className="w-full bg-surface-hover text-text-primary px-4 py-3 rounded-trading font-body-medium hover:bg-border-hover border border-border transition-colors duration-trading-fast"
                >
                  Explore Markets
                </button>
                <button
                  onClick={() => navigate('/login-register')}
                  className="w-full bg-gradient-primary text-white px-4 py-3 rounded-trading font-body-medium hover:opacity-90 transition-opacity duration-trading-fast glow-primary"
                >
                  Get Started
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;